import React, {
  Fragment,
  useState,
  useCallback,
  Children,
  useMemo
} from 'react'
import PropTypes from 'prop-types'
import { Nav } from '@zambezi/sdk/nav'
import { classNames, useGlobalId } from '@zambezi/sdk/common'

/**
 * Create a React Masthead component
 *
 * @param {import('./interfaces').MastheadProps} props
 * @returns {JSX.Element}
 */
export default function Masthead ({
  dark,
  nav,
  title,
  brand,
  Logo,
  logoProps,
  children
}) {
  const [isNavOpen, setNavOpen] = useState(false)
  const mastheadNav = useMemo(() => nav && augmentNav(nav), [nav])
  const menuId = useGlobalId('zb-masthead-menu-')
  const toggleId = useGlobalId('zb-masthead-toggle-')
  const logoClassName = useMemo(() => {
    switch (brand) {
      case 'NatWest':
        return dark ? 'zb-brand-logo-dark-natwest' : 'zb-brand-logo-natwest'
      case 'RoyalBankofScotland':
        return dark ? 'zb-brand-logo-dark-rb' : 'zb-brand-logo-rb'
      case 'UlsterBank':
        return dark
          ? 'zb-brand-logo-dark-ulsterbank'
          : 'zb-brand-logo-ulsterbank'
      case 'NatWestGroup':
        return dark
          ? 'zb-brand-logo-dark-natwestGroup'
          : 'zb-brand-logo-natwestGroup'
      default:
        return 'zb-masthead-brand-logo'
    }
  }, [brand, dark])

  /**
   * @type {import('react').ChangeEventHandler<HTMLInputElement>}
   */
  const handleNavOpen = useCallback(
    ({ type, target }) =>
      setNavOpen(type === 'focus' || (type === 'change' && target.checked)),
    [setNavOpen]
  )

  return (
    <div
      className={classNames('zb-masthead-container', {
        'zb-masthead-dark-container': dark
      })}
    >
      <header className='zb-masthead'>
        {Logo ? (
          <Logo title={title} {...logoProps} />
        ) : (
          <span
            role='img'
            aria-label='Logo'
            className={logoClassName}
            {...logoProps}
          />
        )}

        {title && <div className='zb-masthead-brand-name'>{title}</div>}
        {(children || mastheadNav) && (
          <Fragment>
            <input
              type='checkbox'
              id={toggleId}
              className='zb-masthead-nav-toggle'
              checked={isNavOpen}
              onChange={handleNavOpen}
              aria-haspopup='true'
              aria-controls={menuId}
              aria-expanded={isNavOpen}
              aria-label='Toggle navigation menu'
            />
            <div
              className={classNames('zb-masthead-nav', {
                'zb-masthead-nav-focus-within': isNavOpen
              })}
              onFocus={handleNavOpen}
              onBlur={handleNavOpen}
              aria-labelledby={toggleId}
              id={menuId}
              tabIndex={-1}
            >
              {mastheadNav}
              {children}
            </div>
            <label
              htmlFor={toggleId}
              className='zb-masthead-toggler'
              title='Toggle navigation menu'
            />
          </Fragment>
        )}
      </header>
      {nav}
    </div>
  )
}

/**
 * @param {import('react').ReactNode} child
 * @returns {import('react').ReactNode}
 */
function augmentNav (child) {
  const { type, props } = child || {}
  const otherProps = {}

  if (props && props.id) {
    otherProps.id = `${props.id}-vertical`
  }

  if (child && props) {
    return Object.assign({}, child, {
      props: Object.assign(
        {},
        { ...props, ...otherProps },
        type && type === Nav ? { orientation: 'vertical' } : {},
        {
          children: Children.map(props.children, augmentNav)
        }
      )
    })
  }
  return child
}

Masthead.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node]),
  nav: PropTypes.node,
  dark: PropTypes.bool,
  brand: PropTypes.string,
  logoProps: PropTypes.object,
  Logo: PropTypes.node
}
